import type { ReactNode } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  CircleHelp,
  CircleOff,
  CloudOff,
  Clock3,
  Loader2,
  LockKeyhole,
  ShieldAlert,
  Trash2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { StandardAction } from './StandardAction';
import { StandardCard } from './StandardCard';

export type StandardStateKind = 'loading' | 'empty' | 'error' | 'offline' | 'queued' | 'locked' | 'permission' | 'success' | 'complete' | 'confirm' | 'danger';

const ICONS = {
  loading: Loader2,
  empty: CircleOff,
  error: AlertTriangle,
  offline: CloudOff,
  queued: Clock3,
  locked: LockKeyhole,
  permission: ShieldAlert,
  success: CheckCircle2,
  complete: CheckCircle2,
  confirm: CircleHelp,
  danger: Trash2,
} as const;

const COLORS: Record<StandardStateKind, string> = {
  loading: '#2f5d73',
  empty: '#59636a',
  error: '#8f4d44',
  offline: '#59636a',
  queued: '#a77d38',
  locked: '#765624',
  permission: '#a77d38',
  success: '#55715c',
  complete: '#765624',
  confirm: '#2f5d73',
  danger: '#8f4d44',
};

const isAlert = (kind: StandardStateKind) => kind === 'error' || kind === 'danger';
const isStatus = (kind: StandardStateKind) => ['loading', 'offline', 'queued', 'success', 'complete'].includes(kind);

export interface StandardStateProps {
  kind: StandardStateKind;
  title: string;
  description?: string;
  actionLabel?: string;
  onAction?: () => void;
  secondaryAction?: ReactNode;
  compact?: boolean;
  surface?: 'light' | 'dark';
  align?: 'center' | 'left';
  ariaLive?: 'polite' | 'assertive' | 'off';
  className?: string;
}

export function StandardState({
  kind,
  title,
  description,
  actionLabel,
  onAction,
  secondaryAction,
  compact = false,
  surface = 'light',
  align = 'center',
  ariaLive,
  className,
}: StandardStateProps) {
  const Icon = ICONS[kind];
  const color = COLORS[kind];

  return (
    <StandardCard
      data-standard-state={kind}
      variant={surface === 'dark' ? 'default' : isAlert(kind) ? 'destructive' : kind === 'locked' || kind === 'permission' || kind === 'queued' ? 'warning' : 'default'}
      padding={compact ? 'default' : 'spacious'}
      role={isAlert(kind) ? 'alert' : isStatus(kind) ? 'status' : undefined}
      aria-live={ariaLive === 'off' ? undefined : ariaLive ?? (isAlert(kind) ? 'assertive' : isStatus(kind) ? 'polite' : undefined)}
      className={cn(
        'flex flex-col',
        align === 'center' ? 'items-center text-center' : 'items-start text-left',
        surface === 'dark' && 'border-white/10 bg-[#171715] text-white shadow-[0_14px_34px_rgba(0,0,0,0.2)]',
        className,
      )}
    >
      <div className="flex size-12 items-center justify-center rounded-2xl" style={{ background: `${color}14`, color }}>
        <Icon className={cn('size-6', kind === 'loading' && 'animate-spin')} aria-hidden="true" />
      </div>
      <h3 className={cn('t-title mt-4', surface === 'dark' ? 'text-white' : 'text-[var(--standard-ink,#17151a)]')}>{title}</h3>
      {description ? <p className={cn('mt-2 max-w-md text-sm leading-6', surface === 'dark' ? 'text-white/65' : 'text-[var(--standard-text-secondary,#59636a)]')}>{description}</p> : null}
      {actionLabel && onAction ? (
        <div className={cn('mt-5 flex flex-wrap items-center gap-2', align === 'center' && 'justify-center')}>
          <StandardAction onClick={onAction} variant={kind === 'complete' ? 'earned' : kind === 'danger' ? 'destructive' : 'primary'}>{actionLabel}</StandardAction>
          {secondaryAction}
        </div>
      ) : secondaryAction ? <div className="mt-5">{secondaryAction}</div> : null}
    </StandardCard>
  );
}

export interface StandardStatusProps {
  kind: StandardStateKind;
  title: string;
  description?: string;
  actionLabel?: string;
  onAction?: () => void;
  secondaryAction?: ReactNode;
  surface?: 'light' | 'dark';
  className?: string;
}

export function StandardStatus({
  kind,
  title,
  description,
  actionLabel,
  onAction,
  secondaryAction,
  surface = 'light',
  className,
}: StandardStatusProps) {
  const Icon = ICONS[kind];
  const color = COLORS[kind];

  return (
    <div
      data-standard-status={kind}
      role={isAlert(kind) ? 'alert' : isStatus(kind) ? 'status' : undefined}
      aria-live={isAlert(kind) ? 'assertive' : isStatus(kind) ? 'polite' : undefined}
      className={cn(
        'flex flex-col gap-3 rounded-2xl border p-4 sm:flex-row sm:items-center',
        surface === 'dark'
          ? 'border-white/10 bg-white/[0.045] text-white'
          : isAlert(kind)
            ? 'border-[#d8b7b2] bg-[#fbf3f2] text-[var(--standard-ink,#17151a)]'
            : 'border-[var(--standard-line,#d7dcdf)] bg-[var(--standard-paper,#fcfbf8)] text-[var(--standard-ink,#17151a)]',
        className,
      )}
    >
      <div className="flex min-w-0 flex-1 items-start gap-3">
        <div className="flex size-10 shrink-0 items-center justify-center rounded-xl" style={{ background: `${color}20`, color }}>
          <Icon className={cn('size-5', kind === 'loading' && 'animate-spin')} aria-hidden="true" />
        </div>
        <div className="min-w-0 pt-0.5">
          <p className="text-sm font-extrabold leading-5">{title}</p>
          {description ? <p className={cn('mt-1 text-sm leading-5', surface === 'dark' ? 'text-white/60' : 'text-[var(--standard-text-secondary,#59636a)]')}>{description}</p> : null}
        </div>
      </div>
      {actionLabel && onAction ? (
        <div className="flex shrink-0 flex-wrap items-center gap-2 sm:justify-end">
          <StandardAction onClick={onAction} size="compact" variant={kind === 'danger' ? 'destructive' : 'primary'}>{actionLabel}</StandardAction>
          {secondaryAction}
        </div>
      ) : secondaryAction ? <div className="shrink-0">{secondaryAction}</div> : null}
    </div>
  );
}
